
import { defineComponent, PropType } from 'vue'
import { debounce } from '~/utils/debounce'

export interface FilterGroupT {
  name: string
  icon?: string
  filters: FilterT[]
}

type FilterT =
  | {
      name: string
      type: 'checkbox'
      label: string
      value: string
      checked: boolean
    }
  | {
      name: string
      type: 'range'
      min: number
      max: number
      step: number
      value: number[]
    }

export default defineComponent({
  name: 'UIFilterGroup',
  props: {
    group: {
      type: Object as PropType<FilterGroupT>,
      required: true
    }
  },
  data() {
    return {
      debouncedEmit: debounce((group) => {
        this.$emit('change', group)
      }, 300)
    }
  },
  computed: {
    isSelected() {
      if (this.group.filters.some((filter) => filter.type === 'checkbox')) {
        return this.group.filters.some((filter) => filter.type === 'checkbox' && filter.checked)
      }
      return false
    },
    isRangeChanged() {
      return this.group.filters.some(
        (filter) =>
          filter.type === 'range' &&
          (filter.value[0] !== filter.min || filter.value[1] !== filter.max)
      )
    },
    hasValueSet() {
      return this.isSelected || this.isRangeChanged
    }
  },
  methods: {
    generateFilterKey(filter: FilterT): string {
      return filter.type === 'checkbox'
        ? `${filter.name}-${filter.checked}`
        : `${filter.name}-${filter.value[0]}-${filter.value[1]}`
    },
    handleReset() {
      const newGroups = this.group
      newGroups.filters.forEach((filter) => {
        if (filter.type === 'checkbox') {
          filter.checked = false
        }
        if (filter.type === 'range') {
          filter.value[0] = filter.min
          filter.value[1] = filter.max
        }
      })
      this.$emit('change', newGroups)
    },
    handleFilterChange(
      key: number,
      newValue: { type: 'checkbox'; val: boolean } | { type: 'range'; val: number; index: number }
    ) {
      const filter = this.group.filters[key]

      if (filter.type === 'checkbox' && newValue.type === 'checkbox') {
        filter.checked = newValue.val
      } else if (filter.type === 'range' && newValue.type === 'range') {
        filter.value[newValue.index] = newValue.val
      }

      // Use the debounced emit instead of directly emitting
      this.debouncedEmit(this.group)
    }
  }
})
